import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { LuArrowDown, LuArrowRight, LuArrowUp, LuChevronsUpDown, LuCircleCheck, LuCircleDashed } from 'react-icons/lu';
import { DifficultyLabel } from '../../ui/Labels';
import { useStrings } from '../../tenant/TenantProvider';
import { acceptanceRate } from '../../lib/format';

const STAGGER_LIMIT = 16;

function StatusIcon({ status }) {
  const t = useStrings();
  if (status === 'solved') {
    return <LuCircleCheck className="problem-status problem-status--solved" aria-label={t('problems.statusSolved')} />;
  }
  if (status === 'attempted') {
    return <LuCircleDashed className="problem-status problem-status--attempted" aria-label={t('problems.statusAttempted')} />;
  }
  return <span className="visually-hidden">{t('problems.statusTodo')}</span>;
}

function SortHeader({ column, label, filters, onSort, numeric, className }) {
  const t = useStrings();
  const active = filters.sort === column;
  const Icon = !active ? LuChevronsUpDown : filters.direction === 'asc' ? LuArrowUp : LuArrowDown;
  return (
    <th
      className={[numeric && 'is-numeric', className].filter(Boolean).join(' ') || undefined}
      aria-sort={active ? (filters.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button type="button" className="sort-button" data-active={active} onClick={() => onSort(column)} aria-label={t('problems.sortBy', { column: label })}>
        {label}
        <Icon aria-hidden="true" />
      </button>
    </th>
  );
}

export function ProblemTable({ problems, filters, onSort, activeIndex }) {
  const t = useStrings();
  const navigate = useNavigate();
  const bodyRef = useRef(null);

  useEffect(() => {
    if (activeIndex < 0) return;
    bodyRef.current?.querySelectorAll('tr')[activeIndex]?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const open = (problemId) => navigate(`/problems/${problemId}`, { viewTransition: true });

  return (
    <table className="data-table data-table--interactive problem-table">
      <thead>
        <tr>
          <th className="problem-table__status">
            <span className="visually-hidden">{t('problems.columnStatus')}</span>
          </th>
          <SortHeader column="number" label={t('problems.columnNumber')} filters={filters} onSort={onSort} className="problem-table__number" />
          <th>{t('problems.columnTitle')}</th>
          <SortHeader column="acceptance" label={t('problems.columnAcceptance')} filters={filters} onSort={onSort} numeric className="problem-table__acceptance" />
          <SortHeader column="difficulty" label={t('problems.columnDifficulty')} filters={filters} onSort={onSort} className="problem-table__difficulty" />
          <th className="problem-table__go" aria-hidden="true" />
        </tr>
      </thead>
      <tbody ref={bodyRef} className="stagger">
        {problems.map((problem, index) => {
          const rate = acceptanceRate(problem.acceptedCount, problem.submissionCount);
          return (
            <tr
              key={problem.id}
              className={index === activeIndex ? 'is-active' : undefined}
              style={index < STAGGER_LIMIT ? { '--i': index } : { animation: 'none' }}
              onClick={() => open(problem.id)}
            >
              <td className="problem-table__status">
                <StatusIcon status={problem.status} />
              </td>
              <td className="problem-table__number mono">{problem.id}</td>
              <td className="problem-table__title">
                <a
                  href={`/problems/${problem.id}`}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    open(problem.id);
                  }}
                >
                  {problem.title}
                </a>
                {problem.tags.length > 0 && (
                  <span className="problem-table__tags">
                    {problem.tags.slice(0, 3).map((tag) => (
                      <span key={tag} className="problem-table__tag">
                        {tag}
                      </span>
                    ))}
                  </span>
                )}
              </td>
              <td className="is-numeric problem-table__acceptance muted">{rate === null ? '—' : t('problems.acceptance', { rate })}</td>
              <td className="problem-table__difficulty">
                <DifficultyLabel level={problem.difficulty} className="difficulty-pill" />
              </td>
              <td className="problem-table__go" aria-hidden="true">
                <LuArrowRight />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
